/** Comp a paid subscription until an optional date (TMMPDM-0008). */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy } from '@/api/errors';
import { notPricedCode, TenantNotPricedAlert, type NotPricedCode } from './TenantNotPricedAlert';

interface CompDialogProps {
  open: boolean;
  slug: string;
  subscriptionId: number;
  resellerId: number | null;
  resellerName?: string | null | undefined;
  /** The tenant's currency, for TENANT_CURRENCY_NOT_PRICED (TMMPDM-0025). */
  currency?: string | undefined;
  onClose: () => void;
  onDone: () => void;
}

function toDateTime(date: string): string {
  return date.includes('T') ? date : `${date}T00:00:00.000Z`;
}

export function CompDialog({
  open,
  slug,
  subscriptionId,
  resellerId,
  resellerName,
  currency,
  onClose,
  onDone,
}: CompDialogProps) {
  const { t } = useCopy();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('subscriptions.comp.title', 'Comp subscription')}</DialogTitle>
      {open ? (
        <CompForm
          slug={slug}
          subscriptionId={subscriptionId}
          resellerId={resellerId}
          resellerName={resellerName}
          currency={currency}
          onClose={onClose}
          onDone={onDone}
        />
      ) : null}
    </Dialog>
  );
}

function CompForm({
  slug,
  subscriptionId,
  resellerId,
  resellerName,
  currency,
  onClose,
  onDone,
}: Omit<CompDialogProps, 'open'>) {
  const { t } = useCopy();
  const [until, setUntil] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notPriced, setNotPriced] = useState<NotPricedCode | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    setNotPriced(null);
    try {
      await adminApi.compSubscription(
        slug,
        subscriptionId,
        until ? { until: toDateTime(until) } : {},
      );
      onDone();
    } catch (err: unknown) {
      const notPricedAs = notPricedCode(err);
      if (notPricedAs) {
        setNotPriced(notPricedAs);
      } else {
        setError(
          errorCopy(err, t, t('subscriptions.comp.error', "Couldn't comp the subscription.")),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {notPriced ? (
            <TenantNotPricedAlert
              code={notPriced}
              resellerId={resellerId}
              resellerName={resellerName}
              currency={currency}
            />
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label={t('subscriptions.comp.until', 'Until')}
            type="date"
            value={until}
            onChange={(event) => setUntil(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('subscriptions.subscribeDialog.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {t('subscriptions.comp.submit', 'Comp')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
